<template>
  <v-col>
    <div
      class="scale-buttons justify-center d-flex row no-gutters"
      :class="{
        'flex-nowrap': $vuetify.breakpoint.smAndUp
      }"
    >
      <div
        v-for="button in buttons"
        :key="button.label"
        class="scale-button__container d-flex"
      >
        <platform-button
          primary
          default-case
          class="scale-button d-block grow"
          :class="{
            active: button.value === answer
          }"
          :disabled="isPreview"
          @click="answerQuestion(button.value)"
        >
          <platform-spinner
            class="mx-auto"
            color="white"
            :size="36"
            :width="5"
            :style="{ opacity: button.value === answer && pendingDelay ? 1 : 0 }"
            style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); transition: opacity linear 200ms;"
          />
          <span
            class="mx-auto"
            style="transition: opacity linear 100ms;"
            :style="{ opacity: button.value === answer && pendingDelay ? 0 : 1 }"
          >
            {{ button.label }}
          </span>
        </platform-button>
      </div>
    </div>
  </v-col>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import { ScaleCriteriaOptions } from '@/helpers/questionnaire/questions'
import { ScaleQuestionData } from '@/types/Question'
import PlatformSpinner from '@/components/shared/PlatformSpinner.vue'

export default Vue.extend({
  name: 'SurveyScaleQuestion',
  components: {
    PlatformSpinner
  },
  props: {
    question: { type: Object as PropType<ScaleQuestionData>, required: true },
    value: { type: [String, Number], required: false }, // answer
    isPreview: { type: Boolean, default: false }
  },
  data: () => ({
    pendingDelay: false
  }),
  computed: {
    answer: {
      get (): string | number {
        return this.value
      },
      set (val: string | number): void {
        this.$emit('input', val)
      }
    },
    buttons (): any[] {
      const ScaleButtons = {
        [ScaleCriteriaOptions.Agreement]: [
          { value: 1, label: this.$t('page.viewSurvey.questions.scale.StronglyDisagree') },
          { value: 2, label: this.$t('page.viewSurvey.questions.scale.Disagree') },
          { value: 3, label: this.$t('page.viewSurvey.questions.scale.NeitherAgreement') },
          { value: 4, label: this.$t('page.viewSurvey.questions.scale.Agree') },
          { value: 5, label: this.$t('page.viewSurvey.questions.scale.StronglyAgree') }
        ],
        [ScaleCriteriaOptions.Development]: [
          { value: 1, label: this.$t('page.viewSurvey.questions.scale.VeryUndeveloped') },
          { value: 2, label: this.$t('page.viewSurvey.questions.scale.Undeveloped') },
          { value: 3, label: this.$t('page.viewSurvey.questions.scale.NeitherDevelopment') },
          { value: 4, label: this.$t('page.viewSurvey.questions.scale.Developed') },
          { value: 5, label: this.$t('page.viewSurvey.questions.scale.VeryDeveloped') }
        ],
        [ScaleCriteriaOptions.Importance]: [
          { value: 1, label: this.$t('page.viewSurvey.questions.scale.VeryUnimportant') },
          { value: 2, label: this.$t('page.viewSurvey.questions.scale.Unimportant') },
          { value: 3, label: this.$t('page.viewSurvey.questions.scale.NeitherImportance') },
          { value: 4, label: this.$t('page.viewSurvey.questions.scale.Important') },
          { value: 5, label: this.$t('page.viewSurvey.questions.scale.VeryImportant') }
        ]
      }
      return ScaleButtons[this.question.criteria]
    },
    criteria (): ScaleCriteriaOptions {
      return this.question.criteria
    }
  },
  methods: {
    answerQuestion (answer: any): void {
      if (this.pendingDelay) {
        return
      }
      this.pendingDelay = true
      this.answer = answer
      setTimeout(() => {
        // Artificial delay to allow user to see their answer chosen
        this.$emit('next')
        this.pendingDelay = false
      }, 400)
    }
  }
})
</script>

<style lang="scss" scoped="scoped">
@import '~vuetify/src/styles/settings/_variables';

.scale-buttons {
  .scale-button__container {
    padding: 8px;
    flex-basis: 0;
    flex-grow: 1;
    display: flex;

    .scale-button {
      font-size: 0.9vw;
    }
    @media #{map-get($display-breakpoints, 'xl-only')} {
      height: 160px;
      .scale-button {
        font-size: 1.3em;
      }
    }
    @media #{map-get($display-breakpoints, 'lg-only')} {
      height: 140px;
      .scale-button {
        font-size: 1vw;
      }
    }
    @media #{map-get($display-breakpoints, 'md-only')} {
      height: 120px;
      .scale-button {
        font-size: 1.1vw;
      }
    }
    @media #{map-get($display-breakpoints, 'sm-only')} {
      height: 110px;
      .scale-button {
        font-size: 1.5vw;
      }
    }
    @media #{map-get($display-breakpoints, 'xs-only')} {
      height: 90px;
      max-width: 130px;
      .scale-button {
        font-size: 1.0em;
      }
    }

    .scale-button::v-deep {
      &.v-btn {
        padding: 8px !important;
        white-space: normal;
        font-weight: 600;
        flex-basis: 0;
        flex-grow: 1;
        border-radius: 14px !important;
        transition: all 0.2s ease-out !important;
        position: relative;
        overflow: hidden !important;
        box-shadow: 0 8px 20px -4px rgba(0, 0, 0, 0.5),
                    0 4px 8px rgba(0, 0, 0, 0.2),
                    inset -2px -4px 8px rgba(0, 0, 0, 0.35) !important;
        border: none !important;
        background: linear-gradient(
          150deg,
          var(--v-primary-lighten1) 0%,
          var(--v-primary-base) 40%,
          var(--v-primary-darken2) 100%
        ) !important;

        // Edge highlight - L-shaped with curved corner using borders
        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-top: 3px solid var(--v-primary-lighten2);
          border-left: 3px solid var(--v-primary-lighten2);
          border-top-left-radius: 14px;
          border-right: none;
          border-bottom: none;
          -webkit-mask-image: radial-gradient(
            ellipse 70% 70% at 0% 0%,
            black 0%,
            black 40%,
            transparent 100%
          );
          mask-image: radial-gradient(
            ellipse 70% 70% at 0% 0%,
            black 0%,
            black 40%,
            transparent 100%
          );
          pointer-events: none;
          z-index: 1;
          transition: border-color 0.3s ease-out;
        }

        // Secondary color sweep overlay - on hover
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 200%;
          height: 200%;
          background: linear-gradient(
            150deg,
            var(--v-secondary-lighten2) 0%,
            var(--v-secondary-base) 25%,
            transparent 55%
          );
          opacity: 0;
          transform: translate(-100%, -100%);
          transition: transform 0.4s ease-out, opacity 0.3s ease-out;
          pointer-events: none;
          z-index: 2;
        }

        .v-btn__content {
          position: relative;
          z-index: 1;
        }

        &:hover:not(.v-btn--disabled) {
          transform: scale(1.05) translateY(-3px);
          box-shadow: 0 14px 32px -6px rgba(0, 0, 0, 0.55),
                      0 8px 16px rgba(0, 0, 0, 0.25),
                      inset 0 2px 6px rgba(255, 255, 255, 0.6),
                      inset 0 -4px 8px rgba(0, 0, 0, 0.3) !important;

          &::before {
            opacity: 0.15;
            transform: translate(0, 0);
          }
        }

        &:hover:not(.v-btn--disabled):not(.active) {
          &::after {
            border-top-color: var(--v-secondary-lighten2);
            border-left-color: var(--v-secondary-lighten2);
          }
        }

        &:active:not(.v-btn--disabled) {
          transform: scale(0.97) translateY(2px);
          box-shadow: 0 2px 8px -2px rgba(0, 0, 0, 0.4),
                      inset 0 4px 10px rgba(0, 0, 0, 0.4),
                      inset 0 -2px 4px rgba(255, 255, 255, 0.2) !important;

          &::before {
            opacity: 0;
            transform: translate(100%, 100%);
          }
        }

        &:active:not(.v-btn--disabled):not(.active) {
          &::after {
            border-top-color: var(--v-primary-base);
            border-left-color: var(--v-primary-base);
          }
        }
      }

      &.active.v-btn {
        border: 3px solid var(--v-secondary-base) !important;
        transform: scale(1.02);
        box-shadow: 0 8px 20px -4px rgba(0, 0, 0, 0.4),
                    inset 0 -4px 8px rgba(0, 0, 0, 0.25),
                    inset 0 0 14px -4px var(--v-secondary-base),
                    0 0 18px -4px var(--v-secondary-base) !important;

        &::after {
          opacity: 0;
        }

        &:hover {
          box-shadow: 0 14px 32px -6px rgba(0, 0, 0, 0.55),
                      0 8px 16px rgba(0, 0, 0, 0.25),
                      inset 0 -4px 8px rgba(0, 0, 0, 0.3),
                      inset 0 0 14px -4px var(--v-secondary-base),
                      0 0 18px -4px var(--v-secondary-base) !important;
        }
      }

      &.v-btn--disabled.v-btn {
        color: white !important;
        background: linear-gradient(
          150deg,
          #b0b0b0 0%,
          #8a8a8a 40%,
          #6a6a6a 100%
        ) !important;
        box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.2) !important;

        &::after {
          border-top-color: #c5c5c5 !important;
          border-left-color: #c5c5c5 !important;
        }

        .v-btn__content {
          color: white !important;
        }
      }
    }
  }
}
</style>
